import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import referentesService from "../../../../services/referentesService";
import { showToast } from "../../../../utils/showToast";
import { usePermissions } from "../../../../utils/permissions";
import ComisionistaModal from "../components/ComisionistaModal";

const TIPOS = ["comisionista", "externo", "estudiante", "docente", "administrativo"];
const TIPO_LABEL = { comisionista: "Comisionista", externo: "Externo", estudiante: "Estudiante", docente: "Docente", administrativo: "Administrativo" };
const TIPO_BADGE = { comisionista: "primary", externo: "secondary", estudiante: "info", docente: "success", administrativo: "warning" };

const PAGE_SIZES = [10, 25, 50];

export default function ComisionistasView() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const puedeCrear   = hasPermission("referentes.crear");
  const puedeEditar  = hasPermission("referentes.editar");
  const puedeToggle  = hasPermission("referentes.toggle");
  const puedeRanking = hasPermission("referentes.ranking");

  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [filtros, setFiltros]   = useState({ q: "", tipo: "", activo: "" });
  const [modal, setModal]       = useState({ open: false, item: null });
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pagina: page, porPagina: pageSize };
      if (filtros.q)          params.q      = filtros.q;
      if (filtros.tipo)       params.tipo   = filtros.tipo;
      if (filtros.activo !== "") params.activo = filtros.activo;
      const response = await referentesService.listar(params);
      setItems(Array.isArray(response?.data) ? response.data : []);
      setTotal(Number(response?.total) || 0);
    } catch {
      showToast({ type: "danger", message: "Error al cargar comisionistas." });
    } finally {
      setLoading(false);
    }
  }, [filtros, page, pageSize]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => { setPage(1); }, [filtros]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = (n) => setPage(Math.min(Math.max(1, n), totalPages));

  const handleToggle = async (item) => {
    try {
      await referentesService.toggle(item.id);
      showToast({ type: "success", message: `${item.nombre} ${item.activo ? "desactivado" : "activado"}.` });
      cargar();
    } catch {
      showToast({ type: "danger", message: "Error al cambiar estado." });
    }
  };

  const handleSaved = () => { setModal({ open: false, item: null }); cargar(); };

  return (
    <section className="management-page management-theme-teal">
      <div className="container-fluid">
        <div className="management-card management-header mb-4">
          <div className="d-flex align-items-start flex-wrap gap-2 w-100">
            <div className="flex-grow-1">
              <div className="dashboard-eyebrow">Financiero / Comisionistas</div>
              <h5 className="mb-1">Comisionistas y Referentes</h5>
              <p className="text-muted mb-0">Administra las personas que refieren estudiantes al centro.</p>
            </div>
            <div className="d-flex gap-2">
              {puedeRanking && (
                <button className="btn btn-outline-secondary" onClick={() => navigate("ranking")}>
                  <i className="bi bi-bar-chart me-2"></i>Ranking
                </button>
              )}
              {puedeCrear && (
                <button className="btn management-btn-primary" onClick={() => setModal({ open: true, item: null })}>
                  <i className="bi bi-person-plus me-2"></i>Nuevo
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="management-card mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-5">
              <input
                className="form-control"
                placeholder="Buscar por nombre, identificación o email..."
                value={filtros.q}
                onChange={(e) => setFiltros((f) => ({ ...f, q: e.target.value }))}
              />
            </div>
            <div className="col-6 col-md-3">
              <select className="form-select" value={filtros.tipo}
                onChange={(e) => setFiltros((f) => ({ ...f, tipo: e.target.value }))}>
                <option value="">Todos los tipos</option>
                {TIPOS.map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select" value={filtros.activo}
                onChange={(e) => setFiltros((f) => ({ ...f, activo: e.target.value }))}>
                <option value="">Todos</option>
                <option value="1">Activos</option>
                <option value="0">Inactivos</option>
              </select>
            </div>
            <div className="col-12 col-md-2">
              <button className="btn btn-outline-secondary w-100"
                onClick={() => setFiltros({ q: "", tipo: "", activo: "" })}>
                Limpiar
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="management-card p-0">
          {loading ? (
            <div className="p-4 text-muted">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="p-4 text-muted text-center">No se encontraron resultados.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Identificación</th>
                    <th>Teléfono</th>
                    <th className="text-end">Bonificaciones</th>
                    <th className="text-end">Cobrado</th>
                    <th className="text-end">Pendiente</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} style={{ opacity: item.activo ? 1 : 0.55 }}>
                      <td>
                        <button
                          className="btn btn-link p-0 text-start fw-semibold text-decoration-none"
                          onClick={() => navigate(`${item.id}/perfil`)}
                        >
                          {item.nombre}
                        </button>
                        {item.email && <div className="text-muted small">{item.email}</div>}
                      </td>
                      <td>
                        <span className={`badge rounded-pill bg-${TIPO_BADGE[item.tipo] || "secondary"}-subtle text-${TIPO_BADGE[item.tipo] || "secondary"}`}>
                          {TIPO_LABEL[item.tipo] || item.tipo}
                        </span>
                      </td>
                      <td>{item.identificacion || "-"}</td>
                      <td>{item.telefono || "-"}</td>
                      <td className="text-end">{item.total_bonificaciones ?? 0}</td>
                      <td className="text-end text-success">${parseFloat(item.total_cobrado || 0).toFixed(2)}</td>
                      <td className="text-end text-warning">${parseFloat(item.total_pendiente || 0).toFixed(2)}</td>
                      <td>
                        <span className={`badge rounded-pill ${item.activo ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>
                          {item.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          {puedeEditar && (
                            <button className="btn btn-sm btn-outline-secondary management-action-btn"
                              title="Editar"
                              onClick={() => setModal({ open: true, item })}>
                              <i className="bi bi-pencil-square"></i>
                            </button>
                          )}
                          {puedeToggle && (
                            <button
                              className={`btn btn-sm management-action-btn ${item.activo ? "btn-outline-danger" : "btn-outline-success"}`}
                              title={item.activo ? "Desactivar" : "Activar"}
                              onClick={() => handleToggle(item)}
                            >
                              <i className={`bi ${item.activo ? "bi-toggle-on" : "bi-toggle-off"}`}></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación */}
        {!loading && total > 0 && (
          <div className="management-card d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
            <div className="text-muted small">
              Mostrando {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(total, page * pageSize)} de {total}
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <select className="form-select form-select-sm" style={{ width: "auto" }}
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); goToPage(1); }}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} por página</option>)}
              </select>
              <div className="btn-group">
                <button className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="btn btn-outline-secondary btn-sm" disabled>
                  Página {page} / {totalPages}
                </button>
                <button className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {modal.open && (
        <ComisionistaModal
          item={modal.item}
          onClose={() => setModal({ open: false, item: null })}
          onSaved={handleSaved}
        />
      )}
    </section>
  );
}
